import {FC, useCallback, useMemo} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircle} from "@fortawesome/free-solid-svg-icons/faCircle";
import {Dropdown, DropDownActions} from "./DropDown";
import {BookmakerData, BookmakerObject} from "../../../@types/response";
import {useSelector} from "react-redux";
import {getBookmakers} from "../../../store/selectors/getBookmakers";
import {restartDbotService} from "../../../apis/dbot";

type HeartBeatDropDownProps = {
    product?: string;
    fixedServices?: string[];
};

type ProductKeyInfo = {
    prefix: string;
    service: string | null;
};

const splitProductKey = (productKey: string): ProductKeyInfo => {
    const [prefix, service] = productKey.split("-");
    return {prefix, service: service ?? null};
};

function toTitleCase(value: string) {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function getProductLabel(productKey: string) {
    const labels: Record<string, string> = {
        monitor: "Monitor",
        live: "Live",
        extra: "Extra",
        player: "Player",
        soccer: "Soccer",
        basket: "Basket",
    };
    return labels[productKey] ?? toTitleCase(productKey.replace(/_/g, " "));
}

const getProductsMap = (bookmaker: BookmakerObject) =>
        (bookmaker as any)?.status?.products ?? bookmaker?.products ?? {};

const getServiceName = (productKey: string, bookmakerName: string) =>
        `${productKey.toLowerCase().replace(/-/g, "_")}_${bookmakerName.toLowerCase()}.service`;

export const HeartBeatDropDown: FC<HeartBeatDropDownProps> = ({product, fixedServices}) => {

    const bookmakers: BookmakerData = useSelector(getBookmakers);

    const {filteredBookmakers, productKeysToShow, isError} = useMemo(() => {
        const allBooks = Object.values(bookmakers)
                .filter((book) => book.id > 0)
                .sort((a, b) => (b.name > a.name ? -1 : 1));

        if (!product) {
            const allProductKeys = Array.from(
                    new Set(allBooks.flatMap((book) => Object.keys(getProductsMap(book))))
            ).sort((a, b) => a.localeCompare(b));

            const hasError = allBooks
                    .flatMap((book) => Object.values(getProductsMap(book)))
                    .some((alive) => alive === false);
            return {
                filteredBookmakers: allBooks,
                productKeysToShow: allProductKeys,
                isError: hasError,
            };
        }

        const relevantBooks = allBooks.filter((book) =>
                Object.keys(getProductsMap(book)).some((key) => splitProductKey(key).prefix === product)
        );

        const productKeySet = new Set<string>();
        relevantBooks.forEach((book) => {
            Object.keys(getProductsMap(book)).forEach((key) => {
                if (splitProductKey(key).prefix !== product) return;
                productKeySet.add(key);
            });
        });

        const pinnedServices = fixedServices ?? [];
        pinnedServices.forEach((service) => productKeySet.add(`${product}-${service}`));

        const productKeys = Array.from(productKeySet).sort((a, b) => a.localeCompare(b));
        const pinnedProductKeys = pinnedServices.map((service) => `${product}-${service}`);
        const orderedProductKeys = pinnedServices.length
                ? [
                    ...pinnedProductKeys.filter((k) => productKeySet.has(k)),
                    ...productKeys.filter((k) => !pinnedProductKeys.includes(k)),
                ]
                : productKeys;

        const hasError = relevantBooks
                .flatMap((book) => Object.entries(getProductsMap(book)).filter(([key]) => splitProductKey(key).prefix === product))
                .some(([_, alive]) => alive === false);

        return {
            filteredBookmakers: relevantBooks,
            productKeysToShow: orderedProductKeys,
            isError: hasError,
        };
    }, [fixedServices, bookmakers, product]);

    const onRestart = useCallback(
            async (bookmaker: BookmakerObject, productKey: string) => {
                const info = splitProductKey(productKey);
                const displayLabel = info.service ? `${getProductLabel(info.prefix)} ${getProductLabel(info.service)}` : getProductLabel(info.prefix);
                const serviceName = getServiceName(productKey, bookmaker.name);

                const ok = window.confirm(`Are you sure you want to restart ${displayLabel} for ${bookmaker.name}?`);
                if (!ok) return;

                try {
                    await restartDbotService(serviceName);
                    alert(`Service "${displayLabel}" restarted successfully`);
                } catch (error: any) {
                    alert(`Error on restart service "${displayLabel}": ` + (error?.message ?? error));
                }
            },
            []
    );

    return <Dropdown color={isError ? "bg-toolbar-bgDropDownErr" : "bg-toolbar-bgDropDown"}>
        {filteredBookmakers.map((bookmaker: BookmakerObject) => (
                <DropDownActions
                        key={bookmaker.id}
                        label={bookmaker.name}
                        onClick={() => null}
                        isNetwork={true}
                >
                    {productKeysToShow.map((productKey) => {
                        const alive = getProductsMap(bookmaker)?.[productKey] as any;
                        const available = alive != null;
                        const info = splitProductKey(productKey);
                        const displayLabel = info.service
                                ? `${getProductLabel(info.prefix)} ${getProductLabel(info.service)}`
                                : getProductLabel(info.prefix);

                        return (
                                <span
                                        key={`${bookmaker.id}-${productKey}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (!available) return;
                                            void onRestart(bookmaker, productKey);
                                        }}
                                        className={available ? "cursor-pointer" : "cursor-not-allowed"}
                                        title={available ? `Restart ${displayLabel}` : `${displayLabel} not available`}
                                >
                            <FontAwesomeIcon
                                    icon={faCircle}
                                    className={
                                        alive == null
                                                ? "text-gray-500"
                                                : alive
                                                        ? "text-green-500"
                                                        : "text-red-500"
                                    }
                            />
                        </span>
                        );
                    })}
                </DropDownActions>
        ))}
    </Dropdown>
};
